import type { DocumentDto } from '@dbrain/shared';
import { Button, Icons, Select, Spinner } from '@dbrain/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../api/client.js';
import { useDocument, useDocuments, useRefreshDocuments } from '../../api/documents.js';
import type { CitationView } from '@dbrain/app-core/conversation-state';
import { citationLabel } from '../chat/Markdown.js';
import { ErrorAlert } from '../ErrorAlert.js';
import { SourceLabel } from '../microsoft365/files/SourceLabel.js';
import { DocumentNotes, DocumentPreview, DocumentState, documentSize, EstimateLine, KindBadge } from './DocumentParts.js';

/**
 * A conversation's documents (Phase 12): the sources panel a citation
 * opens (DOC-004, DOC-006), and the list of documents the conversation can
 * search, with their reading state, estimate and notes, adding from the
 * library and taking out (DOC-003, DOC-007).
 */
export function CitationPanel({ citations, selected, onSelect, onClose }: { citations: CitationView[]; selected: number; onSelect: (n: number) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const current = citations.find((c) => c.n === selected) ?? citations[0];
  const doc = useDocument(current?.documentId ?? null);
  if (!current) return null;
  return (
    <aside className="citation-panel" aria-label={t('citations.title')} data-testid="citation-panel" data-n={current.n}>
      <div className="citation-panel__head">
        <strong>{t('citations.title')}</strong>
        <Button size="sm" variant="ghost" aria-label={t('common.close')} onClick={onClose} data-testid="citation-panel-close">
          ×
        </Button>
      </div>
      <div className="citation-panel__chips">
        {citations.map((c) => (
          <button key={c.n} type="button" className={c.n === current.n ? 'pill pill--active' : 'pill'} onClick={() => onSelect(c.n)} data-testid="citation-pick" data-n={c.n}>
            {citationLabel(c, t)}
          </button>
        ))}
      </div>
      <p className="citation-panel__doc">
        <KindBadge kind={doc.data?.kind} name={current.documentName} />
        <strong data-testid="citation-document">{current.documentName}</strong>
      </p>
      {doc.isError ? (
        <>
          <p className="muted">{t('citations.gone')}</p>
          {current.quote ? <blockquote className="doc-preview__quote">“{current.quote}”</blockquote> : null}
        </>
      ) : doc.data ? (
        <DocumentPreview
          doc={doc.data}
          focus={{ kind: current.kind, number: current.number, label: current.label, rowFrom: current.rowFrom, rowTo: current.rowTo, quote: current.quote }}
        />
      ) : (
        <Spinner label={t('common.loading')} />
      )}
    </aside>
  );
}

/** The documents this conversation searches: attachments, ones added from the library, the project's. */
export function ConversationDocumentsMenu({ conversationId, documents }: { conversationId: string; documents: DocumentDto[] }) {
  const { t } = useTranslation();
  const refresh = useRefreshDocuments();
  const library = useDocuments('all', '');
  const [pick, setPick] = useState('');
  const [preview, setPreview] = useState<DocumentDto | null>(null);
  const link = useMutation({
    mutationFn: (documentId: string) => api<DocumentDto[]>('POST', `/api/conversations/${conversationId}/documents`, { documentId }),
    onSuccess: () => {
      setPick('');
      refresh();
    },
  });
  const unlink = useMutation({
    mutationFn: (documentId: string) => api('DELETE', `/api/conversations/${conversationId}/documents/${documentId}`),
    onSuccess: refresh,
  });
  const linked = new Set(documents.map((d) => d.id));
  const choices = (library.data ?? []).filter((d) => d.scope !== 'conversation' && !linked.has(d.id) && d.status !== 'failed');
  const reading = documents.filter((d) => d.status === 'queued' || d.status === 'processing').length;
  return (
    <details className="menu documents-menu" data-testid="documents-menu">
      {/* an icon with the count (SoruKarti.dc.html); the words stay for screen readers */}
      <summary title={t('documents.inConversation')} className="documents-menu__trigger">
        <Icons.documents size={18} />
        <span className="ui-visually-hidden">{t('documents.button', { count: documents.length })}</span>
        {documents.length > 0 ? (
          <span className="documents-menu__count" aria-hidden="true">
            {documents.length}
          </span>
        ) : null}
        {reading > 0 ? <Spinner label="" /> : null}
      </summary>
      <div className="menu__panel documents-menu__panel">
        <ErrorAlert error={link.error ?? unlink.error} />
        {documents.length === 0 ? <p className="muted">{t('documents.noneInConversation')}</p> : null}
        <ul className="doc-list doc-list--compact">
          {documents.map((doc) => (
            <li key={doc.id} className="doc-row" data-testid="conversation-document" data-name={doc.name}>
              <KindBadge kind={doc.kind} />
              <div className="doc-row__body">
                <button type="button" className="link-button doc-row__name" onClick={() => setPreview(doc)} disabled={doc.status === 'failed'}>
                  {doc.name}
                </button>
                <span className="doc-row__meta">
                  <DocumentState doc={doc} />
                  {documentSize(doc, t) ? <span className="muted"> · {documentSize(doc, t)}</span> : null}
                </span>
                {/* part 3: a copy of a Microsoft 365 file says where it came from */}
                {doc.source ? <SourceLabel source={doc.source} quietUpload={false} /> : null}
                <EstimateLine doc={doc} />
                <DocumentNotes doc={doc} />
              </div>
              <Button size="sm" variant="ghost" busy={unlink.isPending && unlink.variables === doc.id} onClick={() => unlink.mutate(doc.id)} aria-label={t('documents.removeFromConversation', { name: doc.name })}>
                ×
              </Button>
            </li>
          ))}
        </ul>
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            if (pick) link.mutate(pick);
          }}
        >
          <Select value={pick} onChange={(e) => setPick(e.target.value)} aria-label={t('documents.addFromLibrary')} data-testid="documents-add-select">
            <option value="">{choices.length > 0 ? t('documents.addFromLibrary') : t('documents.libraryEmpty')}</option>
            {choices.map((doc) => (
              <option key={doc.id} value={doc.id}>
                {doc.name}
              </option>
            ))}
          </Select>
          <Button type="submit" size="sm" variant="secondary" disabled={!pick} busy={link.isPending} data-testid="documents-add">
            {t('common.add')}
          </Button>
        </form>
        <Link to="/documents" className="muted">
          {t('documents.openLibrary')}
        </Link>
        {preview ? (
          <div className="documents-menu__preview">
            <div className="row">
              <strong>{preview.name}</strong>
              <Button size="sm" variant="ghost" onClick={() => setPreview(null)} aria-label={t('common.close')}>
                ×
              </Button>
            </div>
            <DocumentPreview doc={preview} focus={null} />
          </div>
        ) : null}
      </div>
    </details>
  );
}
